import * as React from "react"
import { cn } from "@shared/lib/utils"

type TypographyVariant =
  | "h1" | "h2" | "h3" | "h4"
  | "p" | "blockquote" | "code"
  | "lead" | "large" | "small" | "muted"
  // App-specific variants that replace the CSS utility classes
  | "page-title" | "balance-amount"
  | "section-header" | "section-header-sm"
  | "modal-title" | "modal-title-lg" | "modal-subtitle" | "modal-section-title"
  | "chart-value" | "chart-value-lg"

const Typography = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & {
    variant?: TypographyVariant
    as?: React.ElementType
  }
>(({ className, variant = "p", as, ...props }, ref) => {
  const Comp = as || getDefaultElement(variant)

  return <Comp className={cn(getVariantClasses(variant), className)} ref={ref} {...props} />
})

Typography.displayName = "Typography"

function getDefaultElement(variant: string) {
  switch (variant) {
    case "h1":
    case "page-title":
    case "balance-amount":
      return "h1"
    case "h2":
    case "section-header":
    case "section-header-sm":
      return "h2"
    case "h3":
    case "modal-title":
    case "modal-title-lg":
      return "h3"
    case "h4":
    case "modal-section-title":
      return "h4"
    case "blockquote":
      return "blockquote"
    case "code":
      return "code"
    default:
      return "p"
  }
}

function getVariantClasses(variant: string) {
  switch (variant) {
    case "h1":
      return "scroll-m-20 text-4xl font-bold tracking-tight lg:text-5xl"
    case "h2":
      return "scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0"
    case "h3":
      return "scroll-m-20 text-2xl font-semibold tracking-tight"
    case "h4":
      return "scroll-m-20 text-xl font-semibold tracking-tight"
    case "p":
      return "leading-7 [&:not(:first-child)]:mt-6"
    case "blockquote":
      return "mt-6 border-l-2 pl-6 italic"
    case "code":
      return "relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold"
    case "lead":
      return "text-xl text-muted-foreground"
    case "large":
      return "text-lg font-semibold"
    case "small":
      return "text-sm font-medium leading-5"
    case "muted":
      return "text-sm text-muted-foreground leading-5"
    // App-specific variants — delegate to CSS classes in components.css
    // which use fluid clamp() sizing, relative line-heights, and em-based letter-spacing
    case "page-title":
      return "page-title"
    case "balance-amount":
      return "balance-amount"
    case "section-header":
      return "section-header"
    case "section-header-sm":
      return "section-header-sm"
    case "modal-title":
      return "modal-title"
    case "modal-title-lg":
      return "modal-title-lg"
    case "modal-subtitle":
      return "modal-subtitle"
    case "modal-section-title":
      return "modal-section-title"
    case "chart-value":
      return "chart-value"
    case "chart-value-lg":
      return "chart-value-lg"
    default:
      return ""
  }
}

export type { TypographyVariant }

export { Typography }
